import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { FieldItem } from '@april9/stack9-sdk';
import { Editor, IAllProps as EditorProps } from '@tinymce/tinymce-react';
import cx from 'classnames';

import { useEntityContext } from 'contexts/EntityContext';

import FieldLabel from './FieldLabel';

export type HtmlFieldProps = {
  name: string;
  fieldItem: FieldItem;
  defaultValue?: any;
  entityKey: string;
};

const HtmlField = ({
  name,
  defaultValue,
  fieldItem,
  entityKey,
}: HtmlFieldProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const { insertFileUpload } = useEntityContext();

  const { ui_component_options, description } = fieldItem;

  const readOnly = ui_component_options?.readOnly || false;

  const editorInitProps = useMemo(() => {
    const images_upload_handler: NonNullable<
      EditorProps['init']
    >['images_upload_handler'] = async (blobInfo, progressFn) => {
      try {
        const formData = new FormData();
        formData.append('files', blobInfo.blob());

        const response = await insertFileUpload(
          entityKey,
          name,
          formData,
          _event => {
            progressFn(Math.round((_event.loaded / _event.total) * 100));
          },
        );

        if (!response?.url) {
          throw new Error('Image upload failed - no URL returned');
        }

        return response.url;
      } catch (error) {
        throw new Error(`Image upload error: ${error}`);
      }
    };

    return {
      height: 250,
      forced_root_block: '',
      paste_data_images: true,
      images_upload_handler: images_upload_handler,
      content_css: '/htmlEditor.css',
      plugins: [
        'advlist',
        'autolink',
        'lists',
        'link',
        'image',
        'charmap',
        'preview',
        'anchor',
        'searchreplace',
        'visualblocks',
        'code',
        'fullscreen',
        'insertdatetime',
        'media',
        'table',
        'help',
        'wordcount',
      ],
      toolbar:
        'undo redo | blocks | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help | image | code',
      paste_as_text: false,
      paste_block_drop: false,
      automatic_uploads: false,
      extended_valid_elements:
        'img[class|src|alt|title|width|height|loading=lazy]',
      convert_urls: true,
      relative_urls: false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityKey, name]);

  return (
    <FieldLabel fieldItem={fieldItem}>
      <div data-cy={name} className={cx({ 'is-invalid': errors[name] })}>
        <Controller
          control={control}
          name={name}
          defaultValue={defaultValue}
          render={({ field }) => (
            <Editor
              value={field.value}
              disabled={readOnly}
              onEditorChange={field.onChange}
              onBlur={field.onBlur}
              init={editorInitProps}
            />
          )}
        />

        {description && (
          <small className="form-text text-muted">{description}</small>
        )}
      </div>
    </FieldLabel>
  );
};

export default HtmlField;
